import React, { useState } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { TitleText } from '@/components/ui/TitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';

export interface EmailShoppingListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (email: string) => Promise<void> | void;
  isSubmitting?: boolean;
}

export const EmailShoppingListModal: React.FC<EmailShoppingListModalProps> = ({
  isOpen,
  onClose,
  onSend,
  isSubmitting = false,
}) => {
  const { isDark } = useTheme();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    await onSend(email.trim());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className={`absolute inset-0 ${isDark ? 'bg-black/70' : 'bg-black/50'}`}
        onClick={onClose}
      />
      <Card
        className={`relative w-full max-w-md mx-4 shadow-xl ${
          isDark ? 'bg-neutral-900 border-neutral-700' : 'bg-white'
        }`}
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <TitleText>Email Shopping List</TitleText>
              <BodyText variant="muted">
                Enter the email address to send the current shopping list.
              </BodyText>
            </div>
            <button
              onClick={onClose}
              type="button"
              className={`p-2 rounded-md border transition-colors ${
                isDark
                  ? 'border-neutral-500 hover:bg-neutral-800'
                  : 'border-neutral-200 hover:bg-neutral-100'
              }`}
              aria-label="Close modal"
            >
              <svg
                className={`w-5 h-5 ${isDark ? 'text-neutral-200' : 'text-neutral-700'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <Input
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            disabled={isSubmitting}
            error={error || undefined}
          />

          <div className="flex items-center gap-3">
            <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
              Send Email
            </Button>
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default EmailShoppingListModal;
